/**
 * PortfolioBenchmarkChart — Multi-Benchmark-style comparison chart for the
 * Rebalancing page. Plots every uploaded holding (rebased to % change),
 * SPY / QQQ / DIA / IWM benchmarks, and a market-value-weighted PORTFOLIO
 * composite line. Includes timeframe pills, per-series toggles, a period
 * returns footer, and an AI chart explanation below (same as the stock
 * analysis page).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { RefreshCw, LineChart as LineChartIcon, Bot, Send, CandlestickChart } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import YahooStyleCandlestick from "./YahooStyleCandlestick";
import { detectCandlePattern } from "./MultiBenchmarkChart";
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine, ReferenceArea,
} from "recharts";
import { API } from "../config/api";

const TIMEFRAMES = ["1d", "5d", "10d", "20d", "50d", "100d", "200d", "1y", "3y", "5y"];
const TF_LABEL = { "1d": "1D", "5d": "5D", "10d": "10D", "20d": "20D", "50d": "50D", "100d": "100D", "200d": "200D", "1y": "1Y", "3y": "3Y", "5y": "5Y" };

const MA_CONFIG = [
  { key: "ema5",   label: "5 EMA",   color: "#f97316" },
  { key: "ema10",  label: "10 EMA",  color: "#eab308" },
  { key: "ema20",  label: "20 EMA",  color: "#22c55e" },
  { key: "ema50",  label: "50 EMA",  color: "#06b6d4" },
  { key: "ema200", label: "200 EMA", color: "#6366f1" },
  { key: "sma3",   label: "3 SMA",   color: "#f43f5e" },
  { key: "sma10",  label: "10 SMA",  color: "#d946ef" },
  { key: "sma20",  label: "20 SMA",  color: "#84cc16" },
  { key: "sma50",  label: "50 SMA",  color: "#14b8a6" },
  { key: "sma200", label: "200 SMA", color: "#94a3b8" },
];
const IND_STYLE = {
  rsi: "#fbbf24", mfi: "#38bdf8", vix: "#f87171", volume: "#475569",
  regression: "#f97316", vwap: "#e879f9",
};

const HOLDING_COLORS = [
  "#f59e0b", "#22d3ee", "#ec4899", "#84cc16", "#a78bfa",
  "#fb7185", "#34d399", "#facc15", "#60a5fa", "#f97316",
  "#2dd4bf", "#e879f9", "#a3e635", "#38bdf8", "#fbbf24",
  "#f472b6", "#4ade80", "#c084fc", "#fca5a5", "#67e8f9",
];
const BENCH_CONFIG = [
  { key: "BM_SPY",   label: "S&P 500",        color: "#ffffff" },
  { key: "BM_QQQ",   label: "NASDAQ 100",     color: "#3b82f6" },
  { key: "BM_^IXIC", label: "NASDAQ Comp",    color: "#8b5cf6" },
  { key: "BM_DIA",   label: "Dow 30",         color: "#ef4444" },
  { key: "BM_IWM",   label: "Russell 2000",   color: "#eab308" },
  { key: "BM_GLD",   label: "Gold (GLD)",     color: "#f59e0b" },
  { key: "BM_USO",   label: "Oil (USO)",      color: "#14b8a6" },
  // GICS sector ETFs
  { key: "BM_XLK",   label: "Tech (XLK)",           color: "#60a5fa" },
  { key: "BM_XLC",   label: "Comm Svcs (XLC)",      color: "#a78bfa" },
  { key: "BM_XLY",   label: "Cons Disc (XLY)",      color: "#ec4899" },
  { key: "BM_XLP",   label: "Cons Staples (XLP)",   color: "#fb7185" },
  { key: "BM_XLI",   label: "Industrials (XLI)",    color: "#6366f1" },
  { key: "BM_XLF",   label: "Financials (XLF)",     color: "#22c55e" },
  { key: "BM_XLB",   label: "Materials (XLB)",      color: "#84cc16" },
  { key: "BM_XLRE",  label: "Real Estate (XLRE)",   color: "#94a3b8" },
  { key: "BM_XLU",   label: "Utilities (XLU)",      color: "#facc15" },
  { key: "BM_XLV",   label: "Health Care (XLV)",    color: "#f87171" },
  { key: "BM_XLE",   label: "Energy (XLE)",         color: "#fb923c" },
];
const BENCH_STYLE = Object.fromEntries(BENCH_CONFIG.map(b => [b.key, b.color]));
const BENCH_LABEL = Object.fromEntries(BENCH_CONFIG.map(b => [b.key, b.label]));

function pctFmt(v) {
  return v == null ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}%`;
}

/* ── AI explanation of the weighted portfolio curve ─────────────────── */
function PortfolioChartExplain({ points, timeframe }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const listRef = useRef(null);
  const pointsKey = points?.length ? `${points.length}-${points[points.length - 1]?.v}` : "0";

  const ask = async (question = null, sid = null) => {
    if (!points || points.length < 6) return;
    setBusy(true); setErr(null);
    try {
      // Synthesize candles from the weighted index (base 100) line
      const bars = [];
      for (let i = 1; i < points.length; i++) {
        const o = 100 * (1 + points[i - 1].v / 100);
        const c = 100 * (1 + points[i].v / 100);
        bars.push({ o, c, h: Math.max(o, c), l: Math.min(o, c), t: points[i].t });
      }
      const r = await axios.post(`${API}/chart-ai/explain`, {
        ticker: "PORTFOLIO (weighted index, base 100)",
        timeframe, bars, levels: {}, question, session_id: sid,
      }, { timeout: 60000 });
      setSessionId(r.data.session_id);
      setMessages(m => [...m, { role: "ai", text: r.data.answer }]);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Chart explanation failed — try again.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    setMessages([]); setSessionId(null);
    if (points?.length >= 6) ask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframe, pointsKey]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = () => {
    const q = input.trim();
    if (!q || busy) return;
    setMessages(m => [...m, { role: "user", text: q }]);
    setInput("");
    ask(q, sessionId);
  };

  if (!points || points.length < 6) return null;

  return (
    <Card className="bg-slate-900/40 border-slate-700 h-[600px] flex flex-col mt-3" data-testid="portfolio-chart-explain-card">
      <CardHeader className="pb-2 shrink-0">
        <CardTitle className="text-base text-white flex items-center gap-2">
          <CandlestickChart className="w-5 h-5 text-orange-400" />
          Chart Explanation — AI
          <span className="text-xs text-slate-400 font-normal ml-auto">reads the portfolio line above · {TF_LABEL[timeframe]}</span>
          <button
            type="button"
            onClick={() => { setMessages([]); ask(); }}
            disabled={busy}
            data-testid="portfolio-chart-explain-refresh"
            className="inline-flex items-center gap-1.5 rounded border border-slate-600 bg-slate-800 px-2.5 py-1 text-xs font-semibold text-slate-100 hover:bg-slate-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
            Re-analyze
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col min-h-0 space-y-2 overflow-hidden">
        <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1" data-testid="portfolio-chart-explain-list">
          {messages.map((m, i) => m.role === "user" ? (
            <div key={i} className="flex justify-end">
              <div className="max-w-[85%] rounded-lg bg-purple-800/40 border border-purple-800 px-4 py-2 text-sm text-purple-50">{m.text}</div>
            </div>
          ) : (
            <div key={i} className="rounded-lg bg-slate-950/60 border border-slate-700 px-4 py-3 text-sm text-slate-100 leading-relaxed whitespace-pre-line"
                 data-testid={`portfolio-chart-explain-answer-${i}`}>
              {m.text.split(/\*\*(.+?)\*\*/g).map((part, j) =>
                j % 2 === 1 ? <span key={j} className="block font-bold text-orange-300 mt-2 first:mt-0">{part}</span> : part
              )}
            </div>
          ))}
          {busy && (
            <div className="flex items-center gap-2 text-sm text-slate-400" data-testid="portfolio-chart-explain-thinking">
              <RefreshCw className="w-4 h-4 animate-spin" /> analyzing the portfolio curve…
            </div>
          )}
          {err && <div className="text-sm text-rose-300 bg-rose-950/40 border border-rose-700 rounded p-3" data-testid="portfolio-chart-explain-error">{err}</div>}
        </div>
        <div className="flex gap-2 shrink-0">
          <input
            data-testid="portfolio-chart-explain-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && send()}
            placeholder="Ask about the chart — consolidation, direction, momentum…"
            disabled={busy}
            className="flex-1 rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
          />
          <button
            type="button"
            onClick={send}
            disabled={busy || !input.trim()}
            data-testid="portfolio-chart-explain-send"
            className="inline-flex items-center gap-2 rounded border border-orange-700 bg-orange-800/60 px-4 py-2 text-sm font-semibold text-orange-50 hover:bg-orange-700 disabled:opacity-50 transition-colors"
          >
            <Send className="w-4 h-4" /> Send
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

/* ── Main chart component ───────────────────────────────────────────── */
export default function PortfolioBenchmarkChart({ holdings }) {
  const [timeframe, setTimeframe] = useState("1y");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [visible, setVisible] = useState({
    PORTFOLIO: true, BM_SPY: true, BM_QQQ: true,
    ...Object.fromEntries(BENCH_CONFIG.map(b => [b.key, b.key === "BM_SPY" || b.key === "BM_QQQ"])),
    regression: true, vwap: true, zones: true,
    atr_alert: true, atr_stop: true, atr_zone: true,
    rsi: false, mfi: false, vix: false, volume: false,
    ...Object.fromEntries(MA_CONFIG.map(m => [m.key, false])),
  });
  const [reloadKey, setReloadKey] = useState(0);
  const [chartMode, setChartMode] = useState("line");

  const holdingsKey = JSON.stringify((holdings || []).map(h => [h.ticker, h.market_value]));

  useEffect(() => {
    if (!holdings?.length) { setData(null); return; }
    let cancelled = false;
    setLoading(true); setError(null);
    axios.post(`${API}/daily-rebalancing/portfolio-series`, {
      holdings: holdings.map(h => ({ ticker: h.ticker, market_value: h.market_value })),
      timeframe,
    }, { timeout: 120000 })
      .then(r => {
        if (cancelled) return;
        setData(r.data);
        // Default visibility: PORTFOLIO + SPY/QQQ + top-10 holdings by weight.
        // Every holding still gets a toggle pill; large books start readable.
        setVisible(v => {
          const next = { ...v };
          const ts = r.data.tickers || [];
          const w = r.data.weights || {};
          const top = [...ts].sort((a, b) => (w[b] || 0) - (w[a] || 0)).slice(0, 10);
          ts.forEach(t => { if (next[t] === undefined) next[t] = top.includes(t); });
          return next;
        });
      })
      .catch(e => { if (!cancelled) setError(e?.response?.data?.detail || e?.message || "Failed to load series"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holdingsKey, timeframe, reloadKey]);

  const colorOf = useMemo(() => {
    const map = { PORTFOLIO: "#a855f7", ...BENCH_STYLE };
    (data?.tickers || []).forEach((t, i) => { if (!map[t]) map[t] = HOLDING_COLORS[i % HOLDING_COLORS.length]; });
    return map;
  }, [data?.tickers]);

  const portfolioPoints = useMemo(() =>
    (data?.series || []).filter(r => r.PORTFOLIO != null).map(r => ({ t: r.t, v: r.PORTFOLIO })),
  [data?.series]);

  const dataMaxVol = useMemo(() =>
    Math.max(1, ...(data?.series || []).map(r => r.volume || 0)),
  [data?.series]);

  const candleBars = useMemo(() =>
    (data?.series || []).map(r => ({ ...r, time: r.t })),
  [data?.series]);

  const candlePattern = useMemo(() =>
    candleBars.length ? detectCandlePattern(candleBars) : null,
  [candleBars]);

  if (!holdings?.length) return null;

  const seriesKeys = ["PORTFOLIO", ...(data?.tickers || []), ...BENCH_CONFIG.map(b => b.key)];
  const holdingKeys = ["PORTFOLIO", ...(data?.tickers || [])];
  const benchKeys = BENCH_CONFIG.map(b => b.key);
  const isBenchOnly = (k) => k.startsWith("BM_");
  const toggle = (k) => setVisible(v => ({ ...v, [k]: !v[k] }));

  return (
    <>
      <Card className="bg-slate-900/40 border-slate-700" data-testid="portfolio-benchmark-chart-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-white flex items-center gap-2 flex-wrap">
            <LineChartIcon className="w-5 h-5 text-purple-400" />
            Portfolio Multi-Benchmark Comparison
            <span className="text-xs text-slate-400 font-normal">rebased to % change · weighted PORTFOLIO composite</span>
            <div className="flex items-center gap-1 ml-auto flex-wrap">
              <button
                type="button"
                onClick={() => setReloadKey(k => k + 1)}
                disabled={loading}
                data-testid="portfolio-chart-refresh"
                className="inline-flex items-center gap-1.5 rounded border border-slate-600 bg-slate-800 px-2.5 py-1 text-[10px] font-semibold text-slate-100 hover:bg-slate-700 disabled:opacity-50 transition-colors mr-1"
              >
                <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>
              {/* Line | Candle mode — same as the stock analysis chart */}
              <div className="flex items-center border border-slate-600 rounded overflow-hidden mr-1">
                <button
                  type="button"
                  onClick={() => setChartMode("line")}
                  data-testid="portfolio-chart-mode-line"
                  className={`text-[10px] px-2 py-1 flex items-center gap-1 ${chartMode === "line" ? "bg-emerald-700 text-white" : "text-slate-300 hover:bg-slate-800"}`}
                >
                  <LineChartIcon className="w-3 h-3" /> Line
                </button>
                <button
                  type="button"
                  onClick={() => setChartMode("candle")}
                  data-testid="portfolio-chart-mode-candle"
                  className={`text-[10px] px-2 py-1 flex items-center gap-1 border-l border-slate-600 ${chartMode === "candle" ? "bg-emerald-700 text-white" : "text-slate-300 hover:bg-slate-800"}`}
                >
                  <CandlestickChart className="w-3 h-3" /> Candle
                </button>
              </div>
              {TIMEFRAMES.map(tf => (
                <button
                  key={tf}
                  onClick={() => setTimeframe(tf)}
                  data-testid={`portfolio-chart-tf-${tf}`}
                  className={`px-2 py-1 rounded text-[10px] font-semibold border transition-colors ${
                    timeframe === tf
                      ? "bg-purple-600/40 border-purple-500 text-white"
                      : "border-slate-700 text-slate-400 hover:text-white hover:border-slate-500"
                  }`}
                >
                  {TF_LABEL[tf]}
                </button>
              ))}
            </div>
          </CardTitle>
          {/* Series toggles */}
          <div className="flex items-center gap-1.5 flex-wrap mt-2 max-h-32 overflow-y-auto" data-testid="portfolio-chart-toggles">
            {data?.missing?.length > 0 && !loading && (
              <span className="text-[10px] text-amber-300 mr-1" data-testid="portfolio-chart-missing">
                No data loaded for {data.missing.join(", ")} — hit Refresh
              </span>
            )}
            {(data?.tickers?.length || 0) > 10 && (
              <>
                <button
                  onClick={() => setVisible(v => { const n = { ...v }; (data?.tickers || []).forEach(t => { n[t] = true; }); return n; })}
                  data-testid="portfolio-chart-show-all"
                  className="px-2 py-0.5 rounded-full text-[10px] font-semibold border border-emerald-700 text-emerald-300 hover:bg-emerald-900/40"
                >
                  Show all
                </button>
                <button
                  onClick={() => setVisible(v => { const n = { ...v }; (data?.tickers || []).forEach(t => { n[t] = false; }); return n; })}
                  data-testid="portfolio-chart-hide-all"
                  className="px-2 py-0.5 rounded-full text-[10px] font-semibold border border-slate-700 text-slate-400 hover:bg-slate-800"
                >
                  Hide all
                </button>
              </>
            )}
            {holdingKeys.map(k => (
              <button
                key={k}
                onClick={() => toggle(k)}
                data-testid={`portfolio-chart-toggle-${k}`}
                className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold border transition-all ${
                  visible[k]
                    ? "border-transparent text-black"
                    : "border-slate-700 text-slate-500 bg-transparent"
                }`}
                style={visible[k] ? { backgroundColor: colorOf[k] || "#64748b" } : {}}
              >
                {k === "PORTFOLIO" ? "◆ PORTFOLIO" : k}
                {k !== "PORTFOLIO" && data?.weights?.[k] != null && (
                  <span className="opacity-70 ml-1">{data.weights[k]}%</span>
                )}
              </button>
            ))}
            {/* Regression + VWAP pills (computed on the PORTFOLIO composite) */}
            {["regression", "vwap"].map(k => (
              <button
                key={k}
                onClick={() => toggle(k)}
                data-testid={`portfolio-chart-toggle-${k}`}
                className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold border transition-all ${
                  visible[k] ? "border-transparent text-black" : "border-slate-700 text-slate-500"
                }`}
                style={visible[k] ? { backgroundColor: IND_STYLE[k] } : {}}
              >
                {k === "regression" ? "Regression" : "VWAP"}
              </button>
            ))}
          </div>
          {/* Moving averages row */}
          <div className="flex items-center gap-1.5 flex-wrap mt-1.5" data-testid="portfolio-chart-ma-row">
            <span className="text-[9px] uppercase text-slate-500 tracking-wider mr-1">Moving Avgs</span>
            {MA_CONFIG.map(m => (
              <button
                key={m.key}
                onClick={() => toggle(m.key)}
                data-testid={`portfolio-chart-toggle-${m.key}`}
                className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold border transition-all ${
                  visible[m.key] ? "border-transparent text-black" : "border-slate-700 text-slate-500"
                }`}
                style={visible[m.key] ? { backgroundColor: m.color } : {}}
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full mr-1" style={{ backgroundColor: visible[m.key] ? "#000" : m.color }} />
                {m.label}
              </button>
            ))}
          </div>
          {/* Indicators row */}
          <div className="flex items-center gap-1.5 flex-wrap mt-1.5" data-testid="portfolio-chart-ind-row">
            <span className="text-[9px] uppercase text-slate-500 tracking-wider mr-1">Indicators</span>
            {[
              { key: "rsi", label: "RSI" },
              { key: "mfi", label: "MFI" },
              { key: "vix", label: "VIX" },
              { key: "volume", label: "Volume" },
            ].map(m => (
              <button
                key={m.key}
                onClick={() => toggle(m.key)}
                data-testid={`portfolio-chart-toggle-${m.key}`}
                className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold border transition-all ${
                  visible[m.key] ? "border-transparent text-black" : "border-slate-700 text-slate-500"
                }`}
                style={visible[m.key] ? { backgroundColor: IND_STYLE[m.key] } : {}}
              >
                {m.label}
              </button>
            ))}
            <button
              onClick={() => toggle("zones")}
              data-testid="portfolio-chart-toggle-zones"
              className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-all ${
                visible.zones ? "bg-amber-500/20 border-amber-500 text-amber-200" : "border-slate-700 text-slate-500"
              }`}
            >
              Support / Resistance
            </button>
            <button
              onClick={() => toggle("atr_alert")}
              data-testid="portfolio-chart-toggle-atr-alert"
              className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-all ${
                visible.atr_alert ? "bg-yellow-500/20 border-yellow-500 text-yellow-200" : "border-slate-700 text-slate-500"
              }`}
            >
              1× ATR alert{data?.levels?.atr_alert != null && <span className="ml-1 opacity-80">{data.levels.atr_alert}%</span>}
            </button>
            <button
              onClick={() => toggle("atr_stop")}
              data-testid="portfolio-chart-toggle-atr-stop"
              className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-all ${
                visible.atr_stop ? "bg-orange-500/20 border-orange-500 text-orange-200" : "border-slate-700 text-slate-500"
              }`}
            >
              2× ATR stop{data?.levels?.atr_stop != null && <span className="ml-1 opacity-80">{data.levels.atr_stop}% (ATR {data.levels.atr})</span>}
            </button>
            <button
              onClick={() => toggle("atr_zone")}
              data-testid="portfolio-chart-toggle-atr-zone"
              className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-all ${
                visible.atr_zone ? "bg-rose-500/20 border-rose-500 text-rose-200" : "border-slate-700 text-slate-500"
              }`}
            >
              Warning zone
            </button>
          </div>
          {/* Benchmarks row */}
          <div className="flex items-center gap-1.5 flex-wrap mt-1.5" data-testid="portfolio-chart-bench-row">
            <span className="text-[9px] uppercase text-slate-500 tracking-wider mr-1">Benchmarks</span>
            {benchKeys.map(k => (
              <button
                key={k}
                onClick={() => toggle(k)}
                data-testid={`portfolio-chart-toggle-bench-${k.replace("BM_", "").replace("^", "")}`}
                className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-all ${
                  visible[k] ? "border-transparent text-black" : "border-slate-700 text-slate-500"
                }`}
                style={visible[k] ? { backgroundColor: BENCH_STYLE[k] || "#64748b" } : {}}
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full mr-1" style={{ backgroundColor: visible[k] ? "#000" : (BENCH_STYLE[k] || "#64748b") }} />
                {BENCH_LABEL[k] || k}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {loading && !data ? (
            <div className="h-[420px] flex items-center justify-center text-slate-400 text-sm">
              <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading series…
            </div>
          ) : error ? (
            <div className="h-[120px] flex items-center justify-center text-rose-300 text-sm" data-testid="portfolio-chart-error">{error}</div>
          ) : data?.series?.length ? (
            <>
              {chartMode === "candle" ? (
                <>
                  <YahooStyleCandlestick
                    percentBase={0}
                    ticker="PORTFOLIO"
                    timeframe={timeframe}
                    height={520}
                    bars={candleBars}
                    visible={visible}
                    overlays={[
                      ...seriesKeys.filter(k => k !== "PORTFOLIO").map(k => ({
                        key: k, label: isBenchOnly(k) ? (BENCH_LABEL[k] || k.slice(3)) : k, color: colorOf[k] || "#64748b",
                        strokeWidth: 1.5,
                        strokeDasharray: isBenchOnly(k) ? "6 3" : null,
                      })),
                      ...MA_CONFIG.map(m => ({ key: m.key, label: m.label, color: m.color, strokeWidth: 1.2, strokeDasharray: "4 2" })),
                      { key: "regression", label: "Regression", color: IND_STYLE.regression, strokeWidth: 1.5, strokeDasharray: "8 4" },
                      { key: "vwap", label: "VWAP", color: IND_STYLE.vwap, strokeWidth: 1.3, strokeDasharray: "3 3" },
                    ]}
                    levels={{
                      support: data?.levels?.support,
                      resistance: data?.levels?.resistance,
                      atrAlert: data?.levels?.atr_alert,
                      atrStop: data?.levels?.atr_stop,
                      lastAtr: data?.levels?.atr,
                    }}
                    showZones={visible.zones}
                    showAtrAlert={visible.atr_alert}
                    showAtrStop={visible.atr_stop}
                    showAtrZone={visible.atr_zone}
                  />
                  {/* Latest candlestick pattern — same detector as the stock chart */}
                  {candlePattern && (
                    <div
                      className={`mt-2 rounded border px-3 py-2 text-xs ${
                        candlePattern.bias === "bullish" ? "border-emerald-700 bg-emerald-950/40 text-emerald-100"
                        : candlePattern.bias === "bearish" ? "border-rose-700 bg-rose-950/40 text-rose-100"
                        : "border-slate-700 bg-slate-900/60 text-slate-200"
                      }`}
                      data-testid="portfolio-candle-pattern"
                    >
                      <span className="font-bold">{candlePattern.name}</span>
                      <span className="ml-2 uppercase text-[9px] tracking-wider opacity-80">{candlePattern.bias} · {candlePattern.strength}</span>
                      <div className="mt-1 text-[11px] opacity-90 leading-relaxed">{candlePattern.description}</div>
                    </div>
                  )}
                </>
              ) : (
              <div className="h-[420px]" data-testid="portfolio-chart-plot">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={data.series} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
                    <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                    <XAxis dataKey="t" tick={{ fill: "#64748b", fontSize: 9 }} minTickGap={40} />
                    <YAxis yAxisId="price" tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={v => `${v}%`} width={48} domain={["auto", "auto"]} />
                    {(visible.rsi || visible.mfi) && (
                      <YAxis yAxisId="osc" orientation="right" domain={[0, 100]} tick={{ fill: "#eab308", fontSize: 9 }} width={32} />
                    )}
                    {visible.vix && (
                      <YAxis yAxisId="vix" orientation="right" domain={["auto", "auto"]} tick={{ fill: "#f87171", fontSize: 9 }} width={32} />
                    )}
                    {visible.volume && <YAxis yAxisId="vol" hide domain={[0, dataMaxVol * 4]} />}
                    <Tooltip
                      contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 6, fontSize: 11 }}
                      labelStyle={{ color: "#94a3b8" }}
                      formatter={(v, name) => {
                        if (name === "volume") return [`$${(v / 1e9).toFixed(2)}B`, "Volume ($)"];
                        if (name === "vix") return [Number(v).toFixed(2), "VIX"];
                        if (name === "rsi" || name === "mfi") return [Number(v).toFixed(1), name.toUpperCase()];
                        if (String(name).startsWith("BM_")) return [pctFmt(v), BENCH_LABEL[name] || String(name).slice(3)];
                        return [pctFmt(v), name];
                      }}
                    />
                    <ReferenceLine yAxisId="price" y={0} stroke="#475569" strokeDasharray="4 4" />
                    {/* Support / Resistance zones */}
                    {visible.zones && data?.levels?.resistance != null && (
                      <ReferenceArea yAxisId="price" y1={data.levels.resistance * 0.97} y2={data.levels.resistance * 1.03 + 0.2}
                        fill="#7f1d1d" fillOpacity={0.35} strokeOpacity={0} />
                    )}
                    {visible.zones && data?.levels?.resistance != null && (
                      <ReferenceLine yAxisId="price" y={data.levels.resistance} stroke="#ef4444" strokeDasharray="5 3"
                        label={{ value: `Resistance ${data.levels.resistance}%`, fill: "#fca5a5", fontSize: 9, position: "insideTopLeft" }} />
                    )}
                    {visible.zones && data?.levels?.support != null && (
                      <ReferenceArea yAxisId="price" y1={data.levels.support * 0.97 - 0.2} y2={data.levels.support * 1.03}
                        fill="#14532d" fillOpacity={0.35} strokeOpacity={0} />
                    )}
                    {visible.zones && data?.levels?.support != null && (
                      <ReferenceLine yAxisId="price" y={data.levels.support} stroke="#22c55e" strokeDasharray="5 3"
                        label={{ value: `Support ${data.levels.support}%`, fill: "#86efac", fontSize: 9, position: "insideBottomLeft" }} />
                    )}
                    {/* ATR warning zone + lines */}
                    {visible.atr_zone && data?.levels?.atr_alert != null && data?.levels?.atr_stop != null && (
                      <ReferenceArea yAxisId="price" y1={data.levels.atr_stop} y2={data.levels.atr_alert}
                        fill="#b91c1c" fillOpacity={0.18} strokeOpacity={0} />
                    )}
                    {visible.atr_alert && data?.levels?.atr_alert != null && (
                      <ReferenceLine yAxisId="price" y={data.levels.atr_alert} stroke="#eab308" strokeDasharray="6 3"
                        label={{ value: `1× ATR alert ${data.levels.atr_alert}%`, fill: "#fde047", fontSize: 9, position: "insideRight" }} />
                    )}
                    {visible.atr_stop && data?.levels?.atr_stop != null && (
                      <ReferenceLine yAxisId="price" y={data.levels.atr_stop} stroke="#f97316" strokeDasharray="6 3"
                        label={{ value: `2× ATR stop ${data.levels.atr_stop}% (ATR ${data.levels.atr})`, fill: "#fdba74", fontSize: 9, position: "insideRight" }} />
                    )}
                    {/* Volume bars */}
                    {visible.volume && (
                      <Bar yAxisId="vol" dataKey="volume" fill={IND_STYLE.volume} opacity={0.45} isAnimationActive={false} />
                    )}
                    {/* Holdings + benchmarks + PORTFOLIO */}
                    {seriesKeys.filter(k => visible[k]).map(k => (
                      <Line
                        key={k}
                        yAxisId="price"
                        type="monotone"
                        dataKey={k}
                        name={isBenchOnly(k) ? (BENCH_LABEL[k] || k.slice(3)) : k}
                        stroke={colorOf[k] || "#64748b"}
                        strokeWidth={k === "PORTFOLIO" ? 3 : 1.5}
                        strokeDasharray={isBenchOnly(k) ? "6 3" : undefined}
                        dot={false}
                        connectNulls
                        isAnimationActive={false}
                      />
                    ))}
                    {/* Moving averages */}
                    {MA_CONFIG.filter(m => visible[m.key]).map(m => (
                      <Line key={m.key} yAxisId="price" type="monotone" dataKey={m.key} name={m.label}
                        stroke={m.color} strokeWidth={1.2} strokeDasharray="4 2" dot={false} connectNulls isAnimationActive={false} />
                    ))}
                    {/* Regression + VWAP */}
                    {visible.regression && (
                      <Line yAxisId="price" type="linear" dataKey="regression" name="Regression"
                        stroke={IND_STYLE.regression} strokeWidth={1.5} strokeDasharray="8 4" dot={false} connectNulls isAnimationActive={false} />
                    )}
                    {visible.vwap && (
                      <Line yAxisId="price" type="monotone" dataKey="vwap" name="VWAP"
                        stroke={IND_STYLE.vwap} strokeWidth={1.3} strokeDasharray="3 3" dot={false} connectNulls isAnimationActive={false} />
                    )}
                    {/* Oscillators / VIX */}
                    {visible.rsi && (
                      <Line yAxisId="osc" type="monotone" dataKey="rsi" name="rsi"
                        stroke={IND_STYLE.rsi} strokeWidth={1.2} dot={false} connectNulls isAnimationActive={false} />
                    )}
                    {visible.mfi && (
                      <Line yAxisId="osc" type="monotone" dataKey="mfi" name="mfi"
                        stroke={IND_STYLE.mfi} strokeWidth={1.2} dot={false} connectNulls isAnimationActive={false} />
                    )}
                    {visible.vix && (
                      <Line yAxisId="vix" type="monotone" dataKey="vix" name="vix"
                        stroke={IND_STYLE.vix} strokeWidth={1.2} strokeDasharray="2 2" dot={false} connectNulls isAnimationActive={false} />
                    )}
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              )}
              {/* Regression direction footer */}
              {data?.levels?.regression_slope != null && (
                <div className="text-[10px] text-slate-400 font-mono mt-1 px-1" data-testid="portfolio-chart-regression-note">
                  Regression direction:{" "}
                  <span className={data.levels.regression_slope >= 0 ? "text-emerald-300 font-bold" : "text-rose-300 font-bold"}>
                    {data.levels.regression_slope >= 0 ? "UP" : "DOWN"}
                  </span>{" "}
                  slope/bar {data.levels.regression_slope >= 0 ? "+" : ""}{data.levels.regression_slope}% · ATR(14) {data.levels.atr}%
                </div>
              )}
              {/* Period returns footer */}
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 mt-3" data-testid="portfolio-chart-footer">
                {[...new Set(["PORTFOLIO", ...(data.tickers || []), ...BENCH_CONFIG.map(b => b.key).filter(k => visible[k])])].map(k => (
                  <div key={k} className="rounded border border-slate-800 bg-slate-950/60 px-2.5 py-1.5" data-testid={`portfolio-chart-return-${k}`}>
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: colorOf[k] || "#64748b" }} />
                      <span className="text-[10px] font-mono font-bold text-slate-200 truncate">{isBenchOnly(k) ? (BENCH_LABEL[k] || k.slice(3)) : k}</span>
                      {k !== "PORTFOLIO" && data?.weights?.[k] != null && (
                        <span className="text-[9px] text-slate-500">{data.weights[k]}%</span>
                      )}
                    </div>
                    <div className={`text-[12px] font-mono font-bold ${
                      data.period_returns?.[k] == null ? "text-slate-500"
                        : data.period_returns[k] >= 0 ? "text-emerald-300" : "text-rose-300"
                    }`}>
                      {pctFmt(data.period_returns?.[k])}
                      <span className="text-[9px] text-slate-500 font-normal ml-1">{TF_LABEL[timeframe]}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-[120px] flex items-center justify-center text-slate-500 text-sm">No series data</div>
          )}
        </CardContent>
      </Card>

      {/* AI chart explanation — reads the weighted portfolio curve */}
      <PortfolioChartExplain points={portfolioPoints} timeframe={timeframe} />
    </>
  );
}
